import React from 'react';
import classNames from 'classnames';

import { useIntl } from '../../util/reactIntl';
import { LISTING_BADGES } from '../../config/r2rListingBadges';

import IconShipping from '../IconShipping/IconShipping';

import css from './ListingBadges.module.css';

/**
 * Maps a badge's `iconId` (from r2rListingBadges config) to an icon component.
 * Keeping the registry here lets the config stay free of component imports,
 * which avoids circular dependency issues in the components barrel file.
 */
const ICON_REGISTRY = {
  shipping: IconShipping,
};

/**
 * Renders the set of R2R badges (e.g. WYSIWYG, shipping) that apply to a
 * listing, based on the predicates defined in `src/config/r2rListingBadges.js`.
 *
 * Renders nothing when no badge applies.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.listing API listing entity (uses attributes.publicData)
 * @param {('card'|'detail')} [props.placement='card'] where the badges render.
 *   'card' positions the badges as an overlay (used over the listing image);
 *   'detail' renders them inline (used on the listing detail page).
 * @param {string?} props.className extra style rules in addition to the root class
 * @param {string?} props.rootClassName overwrite the component's own root class
 * @returns {JSX.Element|null} badge list or null when there are no active badges
 */
const ListingBadges = props => {
  const { listing, placement = 'card', className, rootClassName } = props;
  const intl = useIntl();

  const publicData = listing?.attributes?.publicData || {};
  const activeBadges = LISTING_BADGES.filter(badge => badge.isActive(publicData));

  if (activeBadges.length === 0) {
    return null;
  }

  const placementClass = placement === 'detail' ? css.detail : css.card;
  const classes = classNames(rootClassName || css.root, placementClass, className);

  return (
    <div className={classes}>
      {activeBadges.map(badge => {
        const label = intl.formatMessage({ id: badge.labelId });

        if (badge.kind === 'icon') {
          const Icon = ICON_REGISTRY[badge.iconId];
          if (!Icon) {
            return null;
          }
          return (
            <span
              key={badge.id}
              className={classNames(css.badge, css.iconBadge)}
              title={label}
              aria-label={label}
            >
              <Icon className={css.icon} />
            </span>
          );
        }

        return (
          <span
            key={badge.id}
            className={classNames(css.badge, css.textBadge, {
              [css.freeShippingBadge]: badge.id === 'freeShipping',
            })}
          >
            {label}
          </span>
        );
      })}
    </div>
  );
};

export default ListingBadges;
